import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CartProvider } from "./context/CartContext";
import { MenuProvider } from "./context/MenuContext";
import { VendorMenuProvider } from "./context/VendorMenuContext";
import { CouponProvider } from "./context/CouponContext";
import { GlobalRewardsProvider } from "./context/GlobalRewardsContext";
import ProtectedRoute from './components/ProtectedRoute';
import { app } from "./firebase";
import { getAnalytics, logEvent } from "firebase/analytics";
import InfluencerCommission from './components/User/travel/SuperAdmin/InfluencerCommission';

const Navbar = lazy(() => import("./components/Navbar"));
const MenuList = lazy(() => import("./components/User/MenuList"));
const MenuDetail = lazy(() => import("./components/User/MenuDetail"));
const ImageCarousel = lazy(() => import("./components/User/ImageCarousel"));
const VendorMenuPage = lazy(() => import("./components/User/VendorMenuPage"));
const GenerateQRPage = lazy(() => import("./components/GenerateQRPage"));
const CartPage = lazy(() => import("./components/User/CartPage"));
const WayanadLandingPage = lazy(() => import("./components/User/seo/wayanad"));
const VendorDashboard = lazy(() => import('./components/Vendor/VendorDashboard'));
const VendorLogin = lazy(() => import('./components/Vendor/VendorLogin'));
const LiveOrdersPage = lazy(() => import('./components/Vendor/LiveOrdersPage'));
const EditOrderPage = lazy(() => import('./components/Vendor/EditOrderPage'));
const AddVendorAuth = lazy(() => import('./components/Vendor/AddVendorAuth'));
const UserLogin = lazy(() => import('./components/User/UserLogin'));
const OrderHistory = lazy(() => import('./components/Vendor/OrderHistory'));
const UnifiedProfile = lazy(() => import('./components/User/UnifiedProfile'));
const VendorReports = lazy(() => import('./components/Vendor/VendorReports'));
const CouponPage = lazy(() => import('./components/Vendor/CouponPage'));
const RestaurantLibrary = lazy(() => import('./components/User/RestaurantLibrary'));
const TravelLandingPage = lazy(() => import('./components/User/travel/TravelLandingPage'));
const HostTripCreator = lazy(() => import('./components/User/travel/Host/HostTripCreator'));
const LocationManager = lazy(() => import('./components/User/travel/Host/LocationManager'));
const TripsHomePage = lazy(() => import('./components/User/travel/TripsHomePage'));
const InfluencerHostProfile = lazy(() => import('./components/User/travel/Host/InfluencerHostProfile'));
const UserProfileCreation = lazy(() => import('./components/User/travel/UserProfileCreation'));
const TravelSuperAdmin = lazy(() => import('./components/User/travel/SuperAdmin/TravelSuperAdmin'));
const TravelSuperLink = lazy(() => import('./components/User/travel/SuperAdmin/TravelSuperLink'));
const TripLiveOrder = lazy(() => import('./components/User/travel/TripLiveOrder'));
const HostProfilePage = lazy(() => import('./components/User/travel/HostProfile'));
const ResortBooking = lazy(() => import('./components/User/travel/ResortBooking'));
const ResortOnboarding = lazy(() => import('./components/User/travel/ResortOnboarding'));
const ServicesOnboarding = lazy(() => import('./components/User/travel/SuperAdmin/ServicesOnboarding'));
const ActivityOnboarding = lazy(() => import('./components/User/travel/SuperAdmin/ActivityOnboarding'));
const AuthTest = lazy(() => import('./components/User/travel/AuthTest'));
const CreatorLinkLanding = lazy(() => import('./components/User/travel/CreatorLinkLanding'));
const AdminCreatorLinkUsers = lazy(() => import('./components/User/travel/AdminCreatorLinkUsers'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-[#ff4345] border-t-transparent rounded-full animate-spin mx-auto" />
      <p className="mt-3 text-sm text-gray-500">Loading...</p>
    </div>
  </div>
);

const analytics = getAnalytics(app);
logEvent(analytics, 'scroll_depth', { percent: 50 });

function App() {
  return (
    <GlobalRewardsProvider>
      <VendorMenuProvider>
        <MenuProvider>
          <CartProvider>
            <CouponProvider>
            <Router>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<TripsHomePage />} />
              <Route path="/wayanad" element={<WayanadLandingPage />} />
              <Route path="/vendor-login" element={<VendorLogin />} />
              <Route path="/user-login" element={<UserLogin />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/coupon" element={<CouponPage />} />

              {/* Protected Vendor Routes */}
              <Route path="/vendor" element={<ProtectedRoute><VendorDashboard /></ProtectedRoute>} />
              <Route path="/live-order/:vendorId" element={<ProtectedRoute><LiveOrdersPage /></ProtectedRoute>} />
              <Route path="/live-orders" element={<ProtectedRoute><LiveOrdersPage /></ProtectedRoute>} />
              <Route path="/order-history" element={<ProtectedRoute><OrderHistory /></ProtectedRoute>} />
              <Route path="/edit-order/:orderId" element={<ProtectedRoute><EditOrderPage /></ProtectedRoute>} />
              <Route path="/add-vendor" element={<ProtectedRoute><AddVendorAuth /></ProtectedRoute>} />
              <Route path="/generate-qr" element={<ProtectedRoute><GenerateQRPage /></ProtectedRoute>} />
              <Route path="/vendor-reports" element={<ProtectedRoute><VendorReports /></ProtectedRoute>} />
              <Route path="/library" element={<RestaurantLibrary />} />

              {/* User Routes with Navbar */}
              <Route
                path="/user"
                element={
                  <>
                    <Navbar />
                    <ImageCarousel />
                    <MenuList />
                  </>
                }
              />
              <Route
                path="/menu/:id"
                element={
                  <>
                    <Navbar />
                    <MenuDetail />
                  </>
                }
              />
              <Route
                path="/vendor/:vendorId"
                element={<VendorMenuPage />}
              />
              <Route
                path="/profile"
                element={
                  <>
                    <Navbar />
                    <UnifiedProfile />
                  </>
                }
              />
              <Route path="/user/menu-detail" element={<MenuDetail />} />
              <Route
                path="/travel"
                element={<TripsHomePage />}
              />
              <Route
                path="/travel/landing"
                element={<TravelLandingPage />}
              />
              <Route
                path="/host/trip/create"
                element={<HostTripCreator />}
              />
              <Route
                path="/host/location-manager"
                element={<LocationManager />}
              />
              <Route
                path="/host/:hostUid"
                element={<HostProfilePage />}
              />
              <Route
                path="/hostProfile"
                element={<InfluencerHostProfile />}
              />
              <Route
                path="/userProfileCreation"
                element={<UserProfileCreation />}
              />
              <Route
                path="/auth-test"
                element={<AuthTest />}
              />
              <Route
                path="/super-admin"
                element={<TravelSuperAdmin />}
              />
              <Route
                path="/travelSuperLink"
                element={<TravelSuperLink />}
              />
               <Route
                path="/influCommission"
                element={<InfluencerCommission />}
              />
              <Route
                path="/TripLiveOrder"
                element={<TripLiveOrder />}
              />
              <Route
                path="/resort-booking"
                element={<ResortBooking />}
              />
              <Route
                path="/resort-onboarding"
                element={<ResortOnboarding />}
              />
              <Route
                path="/services-onboarding"
                element={<ServicesOnboarding />}
              />
              <Route
                path="/activity-onboarding"
                element={<ActivityOnboarding />}
              />
              <Route
                path="/creator/:creatorName/:tripName/:token"
                element={<CreatorLinkLanding />}
              />
              <Route
                path="/admin/creator-users/:token"
                element={<AdminCreatorLinkUsers />}
              />
            </Routes>
            </Suspense>
            </Router>
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            </CouponProvider>
          </CartProvider>
        </MenuProvider>
      </VendorMenuProvider>
    </GlobalRewardsProvider>
  );
}

export default App;